import React from 'react';
import { useTranslation } from 'react-i18next';

import { CoordinateSystemModel, GeoidModel } from '../../../../typings/api/skymap/rest/v1/.common';
import { SkyMapAxiosServiceFactory } from '../../services/axios/skymap-axios-service-factory';
import { FeatureFlagsManager } from '../../utils/core/feature-flags-manager';
import { getLinearUnit, LinearUnit } from '../../utils/proj4/units';
import { CoordinateManager } from '../../viewer/coordinate-manager';
import { ViewerEnvironmentInstance } from '../../viewer/viewer-environment';
import { errorUtils } from '../api/error-handling';
import { DroneLoader } from '../components/drone-loader/drone-loader';
import { Center } from '../styles/center';
import { ProjectReferenceAdapterBridge } from './project-reference-state/adapter/project-reference-adapter-bridge';
import { ProjectContext } from './project-state/project-state';

type Props = {
  children: React.ReactNode;
  /**
   * Pre-loaded spatial reference for callers that already have the data and cannot
   * fetch it via project endpoints (e.g. shared view, where the share token does
   * not authorise project-scoped reads). When provided, the provider publishes
   * these values directly and skips its own fetch.
   */
  seed?: {
    coordinateSystem?: CoordinateSystemModel;
    geoid?: GeoidModel;
  };
};

export interface ProjectSpatialReferenceContextValue {
  coordinateSystem?: CoordinateSystemModel;
  geoid?: GeoidModel;
  /** The unit that lengths, areas and volumes in the project's coordinate system are in. */
  linearUnit: LinearUnit;
  getCoordinateSystemOrDefault(this: void): CoordinateSystemModel;
  reloadCoordinateSystem(this: void): Promise<void>;
  reloadGeoid(this: void): Promise<void>;
  updateCoordinateSystem(this: void, coordinateSystemId: string): Promise<void>;
  updateGeoid(this: void, geoidId: string | null): Promise<void>;
}

export const ProjectSpatialReferenceContext =
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  React.createContext<ProjectSpatialReferenceContextValue>(undefined!);

// Module-scoped so it survives ProjectReferenceProvider unmount/remount during route-level
// project navigation (ProjectState unmounts children while loading the new project and
// remounts them on success). A React ref would reset to undefined on each mount and miss
// the change, leaving stale projection/origo in CoordinateManager.
let previousProjectKey: string | undefined;

const ProjectReferenceProvider = ({ children, seed }: Props) => {
  const { t } = useTranslation();
  const projectContext = React.useContext(ProjectContext);
  const projectId = projectContext?.project.id;
  const sharedFolderId = projectContext?.sharedFolderId;
  const isSeeded = seed !== undefined;

  const needsLoading = !isSeeded && Boolean(projectId) && !sharedFolderId;

  const [coordinateSystem, setCoordinateSystem] = React.useState<CoordinateSystemModel | undefined>(
    seed?.coordinateSystem,
  );
  const [geoid, setGeoid] = React.useState<GeoidModel | undefined>(seed?.geoid);
  const [loadingCoordinateSystem, setLoadingCoordinateSystem] = React.useState(needsLoading);
  const [loadingGeoid, setLoadingGeoid] = React.useState(needsLoading);

  const loading = loadingCoordinateSystem || loadingGeoid;

  const linearUnit = React.useMemo(
    () => getLinearUnit(coordinateSystem?.proj4 ?? coordinateSystem?.wkt),
    [coordinateSystem],
  );

  React.useEffect(() => {
    ViewerEnvironmentInstance.setSpatialReference({
      projection: coordinateSystem?.proj4,
      projectionName: coordinateSystem?.name,
      geoid: geoid?.id ?? null,
      linearUnit,
    });
  }, [coordinateSystem, geoid, linearUnit]);

  const reloadCoordinateSystem = React.useCallback(async () => {
    if (!projectId || sharedFolderId) {
      setCoordinateSystem(undefined);
      setLoadingCoordinateSystem(false);
      return;
    }

    setLoadingCoordinateSystem(true);
    try {
      const { data } = await SkyMapAxiosServiceFactory.instance
        .createProjectServiceV1()
        .getCoordinateSystem({
          path: { projectId },
        });
      setCoordinateSystem(data);
    } catch (err) {
      if (
        errorUtils.isResponseError(err, 'resource_not_found', 'project:coordinate_system_not_found')
      ) {
        setCoordinateSystem(undefined);
        return;
      }

      throw err;
    } finally {
      setLoadingCoordinateSystem(false);
    }
  }, [projectId, sharedFolderId]);

  const reloadGeoid = React.useCallback(async () => {
    if (!projectId || sharedFolderId) {
      setGeoid(undefined);
      setLoadingGeoid(false);
      return;
    }

    setLoadingGeoid(true);
    try {
      const { data } = await SkyMapAxiosServiceFactory.instance.createProjectServiceV1().getGeoid({
        path: { projectId },
      });
      setGeoid(data);
    } catch (err) {
      if (errorUtils.isResponseError(err, 'resource_not_found', 'project:geoid_not_found')) {
        setGeoid(undefined);
        return;
      }

      throw err;
    } finally {
      setLoadingGeoid(false);
    }
  }, [projectId, sharedFolderId]);

  const updateCoordinateSystem = React.useCallback(
    async (coordinateSystemId: string) => {
      if (!projectId || sharedFolderId) {
        return;
      }

      const result = await SkyMapAxiosServiceFactory.instance
        .createProjectServiceV1()
        .putCoordinateSystem({
          path: {
            projectId,
          },
          body: {
            id: coordinateSystemId,
          },
        });

      setCoordinateSystem(result.data);
    },
    [projectId, sharedFolderId],
  );

  const updateGeoid = React.useCallback(
    async (geoidId: string | null) => {
      if (!projectId || sharedFolderId) {
        return;
      }

      const result = await SkyMapAxiosServiceFactory.instance.createProjectServiceV1().putGeoid({
        path: {
          projectId,
        },
        body: {
          id: geoidId,
        },
      });

      setGeoid(result.data);
    },
    [projectId, sharedFolderId],
  );

  // CoordinateManager.reset() must run only when the user actually switches project/session,
  // not on first mount or when a new provider instance mounts (e.g. opening the File Manager
  // dialog creates a new ProjectState tree and thus a new ProjectReferenceProvider). Resetting
  // on every effect run would clear origo and break Skyview. We track the previous project key
  // at module scope (see `previousProjectKey` above) and call reset() only when it changes
  // from a prior value — module scope ensures the change is detected across provider remounts
  // during route navigation, where a React ref would be reinitialised to undefined.
  React.useEffect(() => {
    if (isSeeded) {
      setCoordinateSystem(seed?.coordinateSystem);
      setGeoid(seed?.geoid);
      setLoadingCoordinateSystem(false);
      setLoadingGeoid(false);
      return;
    }

    if (!projectId || sharedFolderId) {
      setCoordinateSystem(undefined);
      setGeoid(undefined);
      // For shared folders, we don't need to load coordinate system/geoid, so set loading to false
      setLoadingCoordinateSystem(false);
      setLoadingGeoid(false);
      return;
    }

    const load = async () => {
      await Promise.all([reloadCoordinateSystem(), reloadGeoid()]);
      // TODO: remove artificial delay (drone loader review)
      await new Promise((resolve) => setTimeout(resolve, 500));
    };

    const projectKey = `${projectId}-${sharedFolderId ?? ''}`;
    const projectChanged = previousProjectKey !== undefined && previousProjectKey !== projectKey;

    if (projectChanged) {
      CoordinateManager.getInstance().reset();
    }
    previousProjectKey = projectKey;

    void load();
  }, [
    isSeeded,
    seed?.coordinateSystem,
    seed?.geoid,
    projectId,
    sharedFolderId,
    reloadCoordinateSystem,
    reloadGeoid,
  ]);

  const getCoordinateSystemOrDefault = React.useCallback(
    (): CoordinateSystemModel =>
      coordinateSystem ?? {
        id: '',
        name: t('defaultCoordinateSystemName', { ns: 'skyviewScale' }),
        authority: '',
        proj4: 'local',
      },
    [coordinateSystem, t],
  );

  return (
    <ProjectSpatialReferenceContext.Provider
      value={{
        coordinateSystem,
        geoid,
        linearUnit,
        getCoordinateSystemOrDefault,
        reloadCoordinateSystem,
        reloadGeoid,
        updateCoordinateSystem,
        updateGeoid,
      }}
    >
      <ProjectReferenceAdapterBridge />
      {loading ? (
        FeatureFlagsManager.instance.isEnabled('SK-6974-drone-loader') ? (
          <DroneLoader
            message={t('droneLoader.loadingSpatialReferences', { ns: 'components' })}
            mode="page-content"
          />
        ) : (
          <Center>{t('oneMoment', { ns: 'common' })}</Center>
        )
      ) : (
        children
      )}
    </ProjectSpatialReferenceContext.Provider>
  );
};

export { ProjectReferenceProvider };
